import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useUpdateCustomerEquipmentMutation } from "../amcApi";

const WARRANTY_PROVIDERS = [
  { value: "", label: "Not set" },
  { value: "MANUFACTURER", label: "Manufacturer" },
  { value: "SUPPLIER", label: "Supplier" },
  { value: "TEXAS_ALARMS", label: "Texas Alarms" },
];

const STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "REMOVED", label: "Removed from site" },
  { value: "REPLACED", label: "Replaced" },
];

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

/**
 * Edits one equipment row, warranty included - warranty is not a separate screen because
 * it is not a separate record. Clearing "Warranty applies" clears the whole warranty block
 * server-side, so an expired date can never linger on a row that no longer claims one.
 */
const EditEquipmentModal = ({ open, onClose, item }) => {
  const [updateEquipment, { isLoading }] = useUpdateCustomerEquipmentMutation();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!item) {
      setForm(null);
      return;
    }
    setForm({
      quantity: item.quantity ?? 1,
      serialNumber: item.serialNumber || "",
      installationDate: toDateInput(item.installationDate),
      status: item.status || "ACTIVE",
      warrantyApplicable: !!item.warrantyApplicable,
      warrantyProvider: item.warrantyProvider || "",
      warrantyStartDate: toDateInput(item.warrantyStartDate),
      warrantyMonths: item.warrantyMonths ?? "",
      warrantyNotes: item.warrantyNotes || "",
      remarks: item.remarks || "",
    });
  }, [item]);

  if (!open || !item || !form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    if (!(Number(form.quantity) > 0)) {
      toast.error("Quantity must be more than zero.");
      return;
    }
    if (form.serialNumber.trim() && Number(form.quantity) !== 1) {
      toast.error("A serial number identifies one unit, so quantity must be 1.");
      return;
    }

    try {
      await updateEquipment({
        recordGuid: item.recordGuid,
        locationId: item.locationId ?? null,
        quantity: Number(form.quantity),
        serialNumber: form.serialNumber.trim() || null,
        installationDate: form.installationDate || null,
        status: form.status,
        warrantyApplicable: form.warrantyApplicable,
        warrantyProvider: form.warrantyApplicable ? form.warrantyProvider || null : null,
        warrantyStartDate: form.warrantyApplicable ? form.warrantyStartDate || null : null,
        warrantyMonths:
          form.warrantyApplicable && form.warrantyMonths !== "" ? Number(form.warrantyMonths) : null,
        warrantyNotes: form.warrantyApplicable ? form.warrantyNotes || null : null,
        remarks: form.remarks || null,
      }).unwrap();

      toast.success("Equipment updated.");
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || "Could not update equipment.");
    }
  };

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.45)" }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h4 className="mb-0">{item.productName}</h4>
              <small className="text-muted">
                {item.customerName}
                {item.sourceStockOutNo ? ` · from ${item.sourceStockOutNo}` : ""}
                {item.sourcePrototypeName ? ` · from prototype ${item.sourcePrototypeName}` : ""}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  value={form.quantity}
                  onChange={(e) => set({ quantity: e.target.value })}
                />
              </div>
              <div className="col-md-5">
                <label className="form-label">Serial number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="optional"
                  value={form.serialNumber}
                  onChange={(e) => set({ serialNumber: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Installed on</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.installationDate}
                  onChange={(e) => set({ installationDate: e.target.value })}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={(e) => set({ status: e.target.value })}
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <hr className="my-1" />
                <div className="form-check">
                  <input
                    id="warrantyApplicable"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.warrantyApplicable}
                    onChange={(e) => set({ warrantyApplicable: e.target.checked })}
                  />
                  <label className="form-check-label fw-bold" htmlFor="warrantyApplicable">
                    Warranty applies to this equipment
                  </label>
                </div>
              </div>

              {form.warrantyApplicable && (
                <>
                  <div className="col-md-4">
                    <label className="form-label">Provider</label>
                    <select
                      className="form-select"
                      value={form.warrantyProvider}
                      onChange={(e) => set({ warrantyProvider: e.target.value })}
                    >
                      {WARRANTY_PROVIDERS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Starts</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.warrantyStartDate}
                      onChange={(e) => set({ warrantyStartDate: e.target.value })}
                    />
                    <small className="text-muted">Defaults to the installation date.</small>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Period (months)</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={form.warrantyMonths}
                      onChange={(e) => set({ warrantyMonths: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Warranty notes</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.warrantyNotes}
                      onChange={(e) => set({ warrantyNotes: e.target.value })}
                    />
                  </div>
                  {item.warrantyEndDate && (
                    <div className="col-12">
                      <small className="text-muted">
                        Currently expires {toDateInput(item.warrantyEndDate)}. The end date is worked out from
                        the start date and period, so it updates when you save.
                      </small>
                    </div>
                  )}
                </>
              )}

              <div className="col-12">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => set({ remarks: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEquipmentModal;
